import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Field, FormErrors, InjectedFormProps, change, reduxForm } from 'redux-form';

import { useTranslations } from '@lang';

import { basketSelectors } from '@store/basket/selectors';
import { getBasketFormName } from '@utils/basket/addToBasket';
import { getLimitArray } from '@utils/getLimitArray';
import { useIsDesktop } from '@common/hooks/useIsDesktop';
import { lessOrEqual, required, validInteger } from '@utils/validators';

import { byPrice } from '@utils/sorters';
import { Option, OptionsStockCheck, Price, StockCheck } from 'shared-types/Common';

import { Product } from 'shared-types/Product';
import Error from '@common/components/Error';
import { ReduxFormSelect } from '@common/components/FormElements/BlockSelectField';
import OptionPicker from '@common/components/FormElements/OptionPicker';
import Portal from '@common/components/Portal';
import { AmountPicker, AmountPickerProps } from './AmountPicker';
import { OptionsButton, OptionsButtonProps } from './OptionsButton';
import { QuantityPicker } from './QuantityPicker';

import { DiscountPrice } from '@common/components/Price';
import {
  ButtonContainer,
  ComingSoonButton,
  OptionsContainer,
  OptionsSelectWrapper,
  Row,
  SubmitButton,
} from './AddToBasketForm.styles';

const SEARCHABLE_THRESHOLD = 10;

export type AddToBasketFormProps = {
  purchaseLimit: number | null;
  priceToDisplay: {
    price: Price;
  };
  hideOptionLabel: boolean;
  isMonetary?: boolean;
  quantity: number;
  amount?: number;
  selectedOption: Option;
  optionsFieldName: string;
  item: Product;
  stockRemaining?: StockCheck;
} & Omit<AmountPickerProps, 'label' | 'disabled' | 'options'> &
  Pick<OptionsButtonProps, 'id'>;

type AddToBasketFormValues = {
  amount: any;
  quantity: any;
  option?: any;
  voucherOption?: any;
  eventOption?: any;
};

function getItemLimit(quantity, limit) {
  if (!quantity || quantity > limit) {
    return limit;
  }

  return quantity;
}

const validate = (
  values: AddToBasketFormValues,
  props: AddToBasketFormProps
): FormErrors<AddToBasketFormProps> => {
  const errors: any = {};
  const optionsFieldName = props.optionsFieldName || 'option';

  errors[optionsFieldName] = required(values[optionsFieldName], 'optionRequired');
  errors.amount = required(values.amount) || lessOrEqual(values.amount, props.purchaseLimit);
  errors.quantity = required(values.quantity) || validInteger(values.quantity);

  return errors;
};

/**
 * @deprecated Replaced with AddedToBasket/fulfilable for everything except retail products
 */
export const AddToBasketFormComponent: React.FC<
  AddToBasketFormProps & InjectedFormProps<AddToBasketFormValues, AddToBasketFormProps>
> = ({
  error,
  purchaseLimit,
  id,
  priceToDisplay,
  hideOptionLabel,
  isMonetary,
  amount,
  selectedOption,
  change,
  optionsFieldName = 'option',
  item,
  handleSubmit,
  stockRemaining,
}) => {
  const { t } = useTranslations();

  const isDesktop = useIsDesktop();

  const QuantityComponent = isDesktop ? ReduxFormSelect : QuantityPicker;
  const OptionsComponent = isDesktop ? ReduxFormSelect : OptionPicker;

  const monetaryPrice = amount && {
    ...item.price,
    amount: amount * 100,
  };

  const optionsPrice = isMonetary ? monetaryPrice : priceToDisplay.price;

  const selectedStockCheck = stockRemaining?.options.find(
    (item) => item.option.id === selectedOption?.id
  );

  const isOptionUnavailable = selectedStockCheck?.availableQuantity === 0;
  const limit = getItemLimit(selectedStockCheck?.availableQuantity, purchaseLimit);

  const addToBasketDisabled =
    limit === 0 || isOptionUnavailable || stockRemaining?.availableQuantity === 0;

  const optionsMapFilter = (option: Option, i: number) => {
    const stock = stockRemaining?.options[i];
    const availableQuantity = stock?.availableQuantity;
    return {
      ...option,
      availableQuantity,
      isDisabled: availableQuantity === 0,
      value: option.id,
    };
  };

  const maximumVisibleOptions = 10;
  const quantityOptions = isOptionUnavailable
    ? [{ value: '0' }]
    : getLimitArray(limit > maximumVisibleOptions ? maximumVisibleOptions : limit);

  const { options } = item;

  const left = t('product.remaining');

  return (
    <>
      <form onSubmit={handleSubmit} id={id}>
        {error && (
          <Row>
            <Error error={error} />
          </Row>
        )}
        <OptionsContainer data-testid="getAddToBasketContainer">
          {!isMonetary && (
            <OptionsSelectWrapper>
              {options && options.length > 1 && (
                <Field
                  name={optionsFieldName}
                  component={OptionsComponent}
                  props={{
                    options: options.map(optionsMapFilter).sort(byPrice),
                    getOptionLabel: (option) => concatenateOptionLabel(option, left, isMonetary),
                    getOptionValue: (option) => option.id,
                    isSearchable: options && options.length > SEARCHABLE_THRESHOLD,
                    label: hideOptionLabel && t('product.select.option'),
                    placeholder: t('product.selectPlaceholder'),
                    renderMobileOption: ({ name }) => name,
                  }}
                  onChange={(option) => {
                    change('quantity', option.quantity === 0 ? 0 : 1);
                    return option;
                  }}
                />
              )}
            </OptionsSelectWrapper>
          )}
          {isMonetary && (
            <Field
              name={optionsFieldName}
              component={AmountPicker}
              disabled={limit === 0}
              props={{
                options: (options || []).sort(byPrice),
                label: t('select.amount'),
              }}
            />
          )}
          {item.purchaseLimit === 0 ? (
            <ButtonContainer>
              <ComingSoonButton>{t('product.comingSoon')}</ComingSoonButton>
            </ButtonContainer>
          ) : (
            <ButtonContainer>
              <Field
                name="quantity"
                component={QuantityComponent}
                format={(value) => (!isDesktop ? value : { value })}
                normalize={
                  !isDesktop
                    ? (value) => value
                    : ({ value }) => (!isNaN(value) ? parseInt(value, 10) : 1) // eslint-disable-line
                }
                props={{
                  getOptionLabel: (option) => option.value,
                  getOptionValue: (option) => option.value,
                  isCreatable: true,
                  isSearchable: true,
                  options: quantityOptions,
                  placeholder: t('product.select.quantity'),
                  renderMobileOption: ({ value }) => value,
                  maxValue: limit,
                  isDisabled: Boolean(
                    limit === 0 ||
                      (options && options.length > 1 && !selectedOption) ||
                      addToBasketDisabled
                  ),
                }}
              />
              {
                <SubmitButton isPrimary isInline type="submit" disabled={addToBasketDisabled}>
                  {t('product.ctaButton')}
                </SubmitButton>
              }
            </ButtonContainer>
          )}
        </OptionsContainer>
        <Portal selector="#options-button">
          <OptionsButton
            id={id}
            price={optionsPrice as Price}
            isSingleOption={Boolean(options && options.length < 2)}
            onFormSubmit={handleSubmit}
            discount={item.discount}
          />
        </Portal>
      </form>
    </>
  );
};

const concatenateOptionLabel = (
  { quantity, availableQuantity = 0, name, price, discount }: Option & OptionsStockCheck,
  leftString: string,
  isMonetary?: boolean
) => {
  const quantityString = !quantity ? '' : `(${availableQuantity} ${leftString})`;
  return isMonetary ? (
    name
  ) : (
    <>
      {name}
      {' - '}
      <DiscountPrice price={price} discount={discount} /> {quantityString}
    </>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      change,
    },
    dispatch
  );

const mapStateToProps = (state, ownProps) => {
  return {
    form: getBasketFormName(ownProps.item.id),
    amount: basketSelectors.getBasketFormSelector(ownProps.item.id)(state, 'amount'),
    quantity: basketSelectors.getBasketFormSelector(ownProps.item.id)(state, 'quantity'),
  };
};

/**
 * @deprecated Replaced with AddedToBasket/fulfilable for everything except retail products
 */
export const AddToBasketForm: any = connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm<AddToBasketFormValues, Omit<AddToBasketFormProps, 'amount'>>({
    destroyOnUnmount: true,
    enableReinitialize: true,
    validate,
  })(AddToBasketFormComponent) as any
);
